import React, { useEffect, useRef } from 'react';
import { createChart, IChartApi, CandlestickSeries, HistogramSeries } from 'lightweight-charts';
import { KlineData, SignalData } from '../MiniChart/MiniChart';

interface ScoreBreakdown {
  total: number;
  bb_width: number;
  volume_lull: number;
  atr_comp: number;
  oi_growth: number;
  funding: number;
  range_high: number;
  range_low: number;
}

interface ChartModalProps {
  symbol: string;
  klines: KlineData[];
  signal?: SignalData | null;
  lastPrice: number;
  onClose: () => void;
  phase?: string;
  indicators?: Record<string, any>;
  volumeRank?: number;
  turnover24h?: number;
  squeezeScore?: number;
  scoreBreakdown?: ScoreBreakdown;
  fundingRate?: number;
}

// Determine decimal precision based on price magnitude
const pricePrecision = (price: number): number => {
  if (price >= 1000) return 2;
  if (price >= 1) return 2;
  if (price >= 0.01) return 4;
  if (price >= 0.0001) return 6;
  return 8;
};

const formatCompact = (v: number): string => {
  if (v >= 1e9) return '$' + (v / 1e9).toFixed(2) + 'B';
  if (v >= 1e6) return '$' + (v / 1e6).toFixed(1) + 'M';
  if (v >= 1e3) return '$' + (v / 1e3).toFixed(1) + 'K';
  return '$' + v.toFixed(1);
};

const formatIndicatorName = (key: string): string => {
  const names: Record<string, string> = {
    atr: 'ATR',
    atr_sma: 'ATR SMA',
    rsi: 'RSI',
    volume_sma: 'Vol SMA',
    cvd: 'CVD',
    buy_volume: 'Buy Vol',
    sell_volume: 'Sell Vol',
    funding_rate: 'Fund Rate',
    oi_growth_pct: 'OI Growth',
  };
  return names[key] || key.replace(/_/g, ' ').toUpperCase();
};

const ChartModal: React.FC<ChartModalProps> = ({
  symbol, klines, signal, lastPrice, onClose,
  phase, indicators, volumeRank, turnover24h,
  squeezeScore, scoreBreakdown, fundingRate,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  // Single effect: create chart + set data together (modal data is static)
  useEffect(() => {
    // Deduplicate by timestamp (keep last), filter invalid, sort ascending
    const map = new Map<number, KlineData>();
    for (const k of (klines || [])) {
      if (k.time > 0 && k.open > 0 && k.high > 0 && k.low > 0 && k.close > 0) {
        map.set(k.time, k);
      }
    }
    const valid = Array.from(map.values()).sort((a, b) => a.time - b.time);
    if (!containerRef.current || valid.length === 0) return;

    const chart = createChart(containerRef.current, {
      width: containerRef.current.clientWidth,
      height: 420,
      layout: {
        background: { color: '#0f1117' },
        textColor: 'rgba(255,255,255,0.6)',
        fontSize: 11,
      },
      grid: {
        vertLines: { color: 'rgba(255,255,255,0.04)' },
        horzLines: { color: 'rgba(255,255,255,0.04)' },
      },
      rightPriceScale: {
        borderColor: 'rgba(255,255,255,0.1)',
        scaleMargins: { top: 0.05, bottom: 0.2 },
      },
      timeScale: {
        borderColor: 'rgba(255,255,255,0.1)',
        timeVisible: true,
        secondsVisible: false,
      },
      crosshair: {
        mode: 0,
      },
    });

    try {
      const precision = pricePrecision(lastPrice);
      const series = chart.addSeries(CandlestickSeries, {
        upColor: '#34d399',
        downColor: '#ef5350',
        borderVisible: false,
        wickUpColor: '#34d399',
        wickDownColor: '#ef5350',
        priceFormat: { type: 'price', precision, minMove: 1 / Math.pow(10, precision) },
      });

      series.setData(valid as any);

      const volumeSMA = indicators?.volume_sma;
      const volumeSeries = chart.addSeries(HistogramSeries, {
        priceFormat: { type: 'volume' },
        priceScaleId: 'volume',
      } as any);
      chart.priceScale('volume').applyOptions({
        scaleMargins: { top: 0.85, bottom: 0 },
      } as any);

      const volumeData = valid.map(k => ({
        time: k.time as any,
        value: k.volume,
        color: volumeSMA && k.volume > volumeSMA * 3
          ? 'rgba(251, 191, 36, 0.6)'
          : 'rgba(99, 102, 241, 0.25)',
      }));
      volumeSeries.setData(volumeData as any);

      // Add range lines if available
      if (scoreBreakdown && scoreBreakdown.range_high > 0 && scoreBreakdown.range_low > 0) {
        series.createPriceLine({
          price: scoreBreakdown.range_high,
          color: 'rgba(99, 102, 241, 0.6)',
          lineWidth: 1,
          lineStyle: 2,
          title: 'Range High',
        });
        series.createPriceLine({
          price: scoreBreakdown.range_low,
          color: 'rgba(99, 102, 241, 0.6)',
          lineWidth: 1,
          lineStyle: 2,
          title: 'Range Low',
        });
      }

      chart.timeScale().fitContent();

      if (signal) {
        (series as any).setMarkers([{
          time: (signal.timestamp || valid[valid.length - 1].time) as any,
          position: signal.direction === 'LONG' ? 'belowBar' : 'aboveBar',
          color: signal.direction === 'LONG' ? '#34d399' : '#ef5350',
          shape: signal.direction === 'LONG' ? 'arrowUp' : 'arrowDown',
          text: `${signal.direction} @ ${signal.price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
        }]);

        series.createPriceLine({
          price: signal.stop_loss,
          color: '#ef5350',
          lineWidth: 1,
          lineStyle: 2,
          title: 'SL',
        });
        series.createPriceLine({
          price: signal.take_profit,
          color: '#34d399',
          lineWidth: 1,
          lineStyle: 2,
          title: 'TP',
        });
      }
    } catch {
      // Ignore lightweight-charts internal rendering errors
    }

    chartRef.current = chart;

    const ro = new ResizeObserver((entries) => {
      if (chartRef.current) {
        chartRef.current.applyOptions({ width: entries[0].contentRect.width });
      }
    });
    ro.observe(containerRef.current);

    return () => {
      ro.disconnect();
      chart.remove();
      chartRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // mount-only — modal data is a snapshot, never changes

  const fmt = (v: number, digits = 2) =>
    v.toLocaleString('en-US', { minimumFractionDigits: digits, maximumFractionDigits: digits });
  const priceDig = pricePrecision(lastPrice);
  const priceStr = fmt(lastPrice, priceDig);
  const phaseLabel = phase || 'idle';

  return (
    <div className="chart-modal-overlay" onClick={onClose}>
      <div className="chart-modal-content" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="chart-modal-header">
          <div>
            <h2 className="text-viz-text-primary text-xl font-bold">
              {symbol}
              <span className="text-viz-text-secondary text-sm font-normal ml-2">1m</span>
            </h2>
            <span className="text-viz-text-secondary text-base font-mono">${priceStr}</span>
          </div>
          <button onClick={onClose} className="chart-modal-close">&#x2715;</button>
        </div>

        {/* Chart */}
        <div ref={containerRef} className="chart-modal-chart" />

        {/* Score Breakdown Panel */}
        {scoreBreakdown && (
          <div className="chart-modal-score-panel">
            <div className="chart-modal-score-header">
              <span className={`squeeze-score score-${
                (squeezeScore || 0) >= 80 ? 'high' : (squeezeScore || 0) >= 60 ? 'mid' : 'low'
              }`}>Score: {squeezeScore}</span>
              {fundingRate !== undefined && (
                <span className="chart-modal-funding">
                  FR: {(fundingRate * 100).toFixed(4)}%
                </span>
              )}
            </div>
            <div className="chart-modal-score-grid">
              <div><span className="label">BB Squeeze</span><span className="value">{scoreBreakdown.bb_width}/30</span></div>
              <div><span className="label">Vol Lull</span><span className="value">{scoreBreakdown.volume_lull}/20</span></div>
              <div><span className="label">ATR Comp</span><span className="value">{scoreBreakdown.atr_comp}/20</span></div>
              <div><span className="label">OI Growth</span><span className="value">{scoreBreakdown.oi_growth}/15</span></div>
              <div><span className="label">Funding</span><span className="value">{scoreBreakdown.funding}/15</span></div>
            </div>
          </div>
        )}

        {/* Indicators Panel — always visible */}
        <div className="chart-modal-indicators">
          <div className="chart-modal-indicators-header">
            <span className={`mini-chart-phase phase-${phaseLabel}`}>
              {phaseLabel.toUpperCase()}
            </span>
            {indicators?.last_rejection && (
              <span className="rejection-reason">{indicators.last_rejection}</span>
            )}
            {turnover24h !== undefined && turnover24h > 0 && (
              <span className="chart-modal-turnover">
                24h Vol: {formatCompact(turnover24h)} {volumeRank ? `(#${volumeRank})` : ''}
              </span>
            )}
          </div>
          {indicators && Object.keys(indicators).length > 0 && (
            <div className="chart-modal-indicators-grid">
              {Object.entries(indicators)
                .filter(([key]) => key !== 'last_rejection')
                .map(([key, val]) => (
                <div key={key}>
                  <span className="label">{formatIndicatorName(key)}</span>
                  <span className="value">
                    {key === 'funding_rate'
                      ? (val * 100).toFixed(4) + '%'
                      : typeof val === 'number'
                        ? (val !== 0 && Math.abs(val) < 0.01 ? val.toFixed(pricePrecision(val)) : val.toFixed(2))
                        : String(val)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Signal Details */}
        {signal && (
          <div className="chart-modal-signal">
            <div className="chart-modal-signal-header">
              <span className={`chart-modal-direction ${signal.direction === 'LONG' ? 'dir-long' : 'dir-short'}`}>
                {signal.direction === 'LONG' ? '↑ LONG' : '↓ SHORT'}
              </span>
              <span className="chart-modal-confidence">
                {(signal.confidence * 100).toFixed(1)}% confidence
              </span>
            </div>
            <div className="chart-modal-signal-grid">
              <div>
                <span className="label">Entry</span>
                <span className="value">${fmt(signal.price)}</span>
              </div>
              <div>
                <span className="label">Stop Loss</span>
                <span className="value text-red-400">${fmt(signal.stop_loss)}</span>
              </div>
              <div>
                <span className="label">Take Profit</span>
                <span className="value text-green-400">${fmt(signal.take_profit)}</span>
              </div>
              <div>
                <span className="label">Strategy</span>
                <span className="value">{signal.strategy.replace('_', ' ')}</span>
              </div>
            </div>
            {signal.metadata && Object.keys(signal.metadata).length > 0 && (
              <div className="chart-modal-metadata">
                {Object.entries(signal.metadata).map(([key, val]) => (
                  <span key={key} className="metadata-tag">
                    {key}: {typeof val === 'number' ? val.toFixed(2) : String(val)}
                  </span>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ChartModal;
